import { useState, useEffect } from 'react';
import { useMediaQuery } from './useMediaQuery';

/**
 * Breakpoint type matching Tailwind's default breakpoints
 */
export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/**
 * Breakpoint values in pixels
 */
export const breakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

/**
 * Interface for the breakpoint hook return value
 */
export interface UseBreakpointsReturn {
  /** Current breakpoint name */
  breakpoint: Breakpoint;
  /** Is the screen extra small (< 640px) */
  isXs: boolean;
  /** Is the screen small (≥ 640px) */
  isSm: boolean;
  /** Is the screen medium (≥ 768px) */
  isMd: boolean;
  /** Is the screen large (≥ 1024px) */
  isLg: boolean;
  /** Is the screen extra large (≥ 1280px) */
  isXl: boolean;
  /** Is the screen 2x extra large (≥ 1536px) */
  is2xl: boolean;
  /** Helper: is mobile (< 768px) */
  isMobile: boolean;
  /** Helper: is tablet (≥ 768px and < 1024px) */
  isTablet: boolean;
  /** Helper: is desktop (≥ 1024px) */
  isDesktop: boolean;
  /** Current viewport width in pixels */
  width: number;
  /** Current viewport height in pixels */
  height: number;
}

/**
 * Get the current breakpoint based on window width
 */
const getBreakpoint = (width: number): Breakpoint => {
  if (width >= breakpoints['2xl']) return '2xl';
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  if (width >= breakpoints.sm) return 'sm';
  return 'xs';
};

/**
 * Hook to get current breakpoint and responsive helpers
 *
 * @example
 * ```tsx
 * const { isMobile, isTablet, isDesktop, breakpoint } = useBreakpoints();
 *
 * return (
 *   <div>
 *     {isMobile && <MobileView />}
 *     {isDesktop && <DesktopView />}
 *     <p>Current breakpoint: {breakpoint}</p>
 *   </div>
 * );
 * ```
 */
export const useBreakpoints = (): UseBreakpointsReturn => {
  // Use individual media queries for better performance
  const isSm = useMediaQuery('(min-width: 640px)');
  const isMd = useMediaQuery('(min-width: 768px)');
  const isLg = useMediaQuery('(min-width: 1024px)');
  const isXl = useMediaQuery('(min-width: 1280px)');
  const is2xl = useMediaQuery('(min-width: 1536px)');

  // Track viewport dimensions
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Set initial dimensions
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Derive breakpoint from flags
  const breakpoint = getBreakpoint(dimensions.width);

  // Derive isXs (everything that's not sm or above)
  const isXs = !isSm;

  // Helpers
  const isMobile = !isMd; // < 768px
  const isTablet = isMd && !isLg; // >= 768px and < 1024px
  const isDesktop = isLg; // >= 1024px

  return {
    breakpoint,
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    is2xl,
    isMobile,
    isTablet,
    isDesktop,
    width: dimensions.width,
    height: dimensions.height,
  };
};

/**
 * Hook to check if current breakpoint matches or exceeds a target breakpoint
 *
 * @param target Target breakpoint to check against
 * @returns true if current breakpoint is >= target breakpoint
 *
 * @example
 * ```tsx
 * const isLargeOrAbove = useBreakpointValue('lg');
 * // Returns true if viewport is lg, xl, or 2xl
 * ```
 */
export const useBreakpointValue = (target: Breakpoint): boolean => {
  const { width } = useBreakpoints();
  return width >= breakpoints[target];
};

/**
 * Hook to get a value based on the current breakpoint
 * Similar to Chakra UI's useBreakpointValue
 *
 * @param values Object with breakpoint keys and corresponding values
 * @returns The value for the current breakpoint (or closest smaller breakpoint)
 *
 * @example
 * ```tsx
 * const columns = useBreakpointResponsiveValue({
 *   xs: 1,
 *   sm: 2,
 *   md: 3,
 *   lg: 4,
 *   xl: 5,
 * });
 *
 * return <div className={`grid grid-cols-${columns}`}>...</div>;
 * ```
 */
export const useBreakpointResponsiveValue = <T,>(
  values: Partial<Record<Breakpoint, T>>
): T | undefined => {
  const { breakpoint } = useBreakpoints();

  // Priority order from largest to smallest
  const order: Breakpoint[] = ['2xl', 'xl', 'lg', 'md', 'sm', 'xs'];

  // Find the current breakpoint's index
  const currentIndex = order.indexOf(breakpoint);

  // Look for a value at current breakpoint or fallback to smaller ones
  for (let i = currentIndex; i < order.length; i++) {
    const bp = order[i];
    if (values[bp] !== undefined) {
      return values[bp];
    }
  }

  return undefined;
};
